import { createApiSlice } from '@/shared/features/query/config/query.build';
import type { ReduxQueryClient } from '@/shared/features/query/domain/interfaces/QueryClient';
import type { AppDispatch } from '@/app/store/store';
import { executeQuery } from '@/shared/features/query/utils/queryUtils';
import { getApiBaseUrl } from '@/core/utils/apiUtils';
import type { SimilarDesignsResponseDTO } from '../dto/similarDesigns.dto';
import { similarDesignsDtoToItems } from '../mappers/similarDesigns.mapper';
import type { SimilarDesignsRepository } from '../../domain/interfaces/SimilarDesignsRepository';
import type { SimilarDesignItem } from '../../domain/models/SimilarDesignItem';

export type GetSimilarDesignsArgs = {
  designId: string;
};

const similarDesignsApiSlice = createApiSlice({
  clientId: 'similarDesignsApi',
  tagTypes: ['SimilarDesigns'],
  endpoints: {
    getSimilarDesigns: {
      url: (args: unknown) => {
        const { designId } = (args || {}) as GetSimilarDesignsArgs;
        return `${getApiBaseUrl()}/similar-design/design-group/${designId}`;
      },
      method: 'GET',
      transformResponse: (response: unknown): Record<string, unknown> => {
        const dto = (response ?? {}) as SimilarDesignsResponseDTO;
        return { items: similarDesignsDtoToItems(dto) } as unknown as Record<string, unknown>;
      },
      keepUnusedDataFor: 120,
    },
  },
});

export const similarDesignsApi = similarDesignsApiSlice.queryClient;
export const similarDesignsApiRedux = similarDesignsApiSlice.queryClient as ReduxQueryClient;

export class SimilarDesignsAdapter implements SimilarDesignsRepository {
  constructor(private readonly dispatch: AppDispatch) {}

  async fetchSimilarDesigns(designId: string): Promise<SimilarDesignItem[]> {
    const initiateResult = similarDesignsApi.endpoints.getSimilarDesigns.initiate({ designId });
    const plain = await executeQuery<{ items: SimilarDesignItem[] }>(initiateResult, this.dispatch);
    return plain.items ?? [];
  }
}
